import { apiErrorMessage } from './apiError';

/**
 * The single place that knows how to talk to this backend.
 *
 * Before this module, 36 components hand-rolled the same three-header call and
 * got it wrong in four different ways:
 *
 *   D1  ReviewQueue/ChefConfirm  read localStorage 'organization_id'
 *   D5  App.js ProposalsPage     omitted X-Organization-Id entirely
 *   D6  SafetyHealth/RoomBooking/EnhancedEventPlanning read 'activeOrgId'
 *   D2  three components         fell back to an absolute http://localhost:8000
 *
 * D1 and D6 are the same root cause: reading a localStorage key that login
 * never writes. A real UI login writes ONLY `current_organization_id`
 * (App.js handleAuth) — `organization_id` is written solely by the legacy
 * OnboardingWizard path, and `activeOrgId` is React state in App.js that is
 * never persisted at all, so `getItem('activeOrgId')` is unconditionally null.
 *
 * Do not add another org-id key. Do not read these keys directly in a
 * component — e2e/boundary.spec.mjs greps for both and fails the build.
 */

/** The one canonical org-id key. Written by App.js on login/register. */
export const ORG_KEY = 'current_organization_id';
export const TOKEN_KEY = 'token';

/**
 * Legacy keys, listed so the ban is documented and greppable. These are NOT
 * read as a fallback, deliberately:
 *
 *   - `activeOrgId` has no writer anywhere in the codebase, so a fallback to
 *     it is dead code that only makes the real bug harder to see.
 *   - `organization_id` is written once by the legacy OnboardingWizard and
 *     never updated on org switch. Falling back to it would send a STALE org
 *     id — quietly scoping the user to the wrong tenant. A visible 400 is
 *     strictly safer than invisible cross-tenant access.
 *
 * If the canonical key is missing the user is not properly authenticated;
 * omit the header and let the backend reject the call.
 */
export const BANNED_ORG_KEYS = Object.freeze(['organization_id', 'activeOrgId']);

function read(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    // Safari private mode / disabled storage — behave as logged-out rather
    // than throwing inside a render.
    return '';
  }
}

export function getToken() {
  return read(TOKEN_KEY);
}

/**
 * Resolve the active organization id from the canonical key only.
 * See BANNED_ORG_KEYS for why there is deliberately no fallback.
 */
export function resolveOrgId() {
  return read(ORG_KEY);
}

/**
 * Base URL for API calls. Always relative in a deployed stack.
 *
 * An absolute host here is the D2 footgun: frontend/nginx.conf proxies `/api/`
 * same-origin precisely so the browser never needs to know a backend address.
 * An absolute fallback passes every server-side check and fails only in the
 * customer's browser — and, worse, transmits the user's session token to
 * whatever happens to occupy that port on their machine.
 */
export function apiBase() {
  return process.env.REACT_APP_BACKEND_URL || '';
}

/**
 * Build the standard header set.
 *
 * Empty values are OMITTED rather than sent as ''. An empty header is not a
 * neutral default — the backend rejects it with
 * `400 X-Organization-Id header is required`, which is precisely how D1
 * surfaced. Omitting makes the "not ready yet" case distinguishable from the
 * "wrong value" case in logs.
 */
export function authHeaders(options = {}) {
  const {
    orgId = resolveOrgId(),
    locationId = '',
    department = '',
    json = false,
    token = getToken(),
  } = options;

  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Organization-Id'] = orgId;
  if (locationId) headers['X-Location-Id'] = locationId;
  if (department) headers['X-Department'] = department;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

/** True when there is enough context to make an org-scoped call. */
export function hasOrgContext() {
  return Boolean(getToken() && resolveOrgId());
}

/**
 * Repair a session that has a valid token but no canonical org id, and drop
 * any banned legacy keys left over from before this module existed.
 *
 * Needed because resolveOrgId() has no legacy fallback (see BANNED_ORG_KEYS).
 * Without this, a session holding only the legacy `organization_id` would 400
 * on every org-scoped screen until the token expired and the user logged in
 * again. Resolving from /api/platform/context instead of the legacy key keeps
 * the value SERVER-AUTHORITATIVE, so this repairs the session without
 * reintroducing the stale-org-id / wrong-tenant hazard.
 *
 * Safe to call on every boot: it is a no-op once the canonical key is present.
 */
export async function ensureOrgContext(token = getToken()) {
  for (const key of BANNED_ORG_KEYS) {
    try {
      if (localStorage.getItem(key) !== null) localStorage.removeItem(key);
    } catch {
      /* storage unavailable */
    }
  }

  const existing = resolveOrgId();
  if (existing || !token) return existing;

  try {
    const res = await fetch(`${apiBase()}/api/platform/context`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return '';
    const ctx = await res.json();
    const orgId = ctx?.organizations?.[0]?.id;
    if (orgId) {
      localStorage.setItem(ORG_KEY, orgId);
      return orgId;
    }
  } catch {
    /* offline or context unavailable — callers fall back to omitting the header */
  }
  return '';
}

/**
 * Error carrying the HTTP status so callers can branch on 401/403 without
 * re-parsing a message string. `message` is already human-readable via
 * apiErrorMessage(), which normalises FastAPI's string-or-array `detail`.
 */
export class ApiError extends Error {
  constructor(status, detail, url) {
    super(apiErrorMessage(detail, `Request failed (${status})`));
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.url = url;
  }

  /** Distinguishes "you may not" (D4: show an explicit state) from a crash. */
  get isForbidden() {
    return this.status === 403;
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
}

/**
 * Make an authenticated API call.
 *
 * `path` must be root-relative ('/api/...'). Passing an absolute URL throws —
 * that is D2 being made impossible rather than merely fixed.
 */
export async function apiFetch(path, options = {}) {
  const {
    orgId,
    locationId,
    department,
    requireOrg = false,
    responseType = 'json',
    headers: extraHeaders,
    ...init
  } = options;

  if (/^[a-z]+:\/\//i.test(path)) {
    throw new Error(
      `apiFetch requires a root-relative path, got absolute URL "${path}". ` +
        `API calls must be same-origin via nginx's /api/ proxy (defect D2).`,
    );
  }

  const resolvedOrg = orgId !== undefined ? orgId : resolveOrgId();
  if (requireOrg && !resolvedOrg) {
    throw new ApiError(
      0,
      'No active organization. Reload or pick an organization to continue.',
      path,
    );
  }

  const isJsonBody = init.body !== undefined && typeof init.body === 'string';
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      ...authHeaders({
        orgId: resolvedOrg,
        locationId,
        department,
        json: isJsonBody,
      }),
      ...extraHeaders,
    },
  });

  if (!res.ok) {
    let detail = null;
    try {
      detail = (await res.json())?.detail ?? null;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail, path);
  }

  if (res.status === 204) return null;
  if (responseType === 'blob') return res.blob();
  return res.json();
}
